import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "@/features/utils/i18n";
import { GlobeIcon, SparkleIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { OnboardingLayout } from "./layout";

/**
 * Step 1 — the only thing we ask for up front.
 *
 * Everything else in the wizard is pre-filled from what this yields, so this
 * screen asks for one thing and explains the trade: tell us where to look, skip
 * the form. Submitting it starts the analysis in the background — the client
 * moves straight to the next question and never watches it run.
 *
 * A client with no website is not a dead end and must not be treated as one:
 * one sentence about what they do is a perfectly good input, and the flow past
 * this point is identical.
 */
export function StepWebsite({
  website,
  description,
  onStart,
  isStarting,
  error,
}: {
  website: string;
  description: string;
  onStart: (input: { url?: string; description?: string }) => void;
  isStarting?: boolean;
  error: string | null;
}) {
  const { t } = useTranslation("onboarding");

  const [mode, setMode] = useState<"url" | "description">(
    description && !website ? "description" : "url",
  );
  const [url, setUrl] = useState(website);
  const [text, setText] = useState(description);

  useEffect(() => setUrl(website), [website]);
  useEffect(() => setText(description), [description]);

  const value = mode === "url" ? url : text;
  const canSubmit = value.trim().length > 0 && !isStarting;

  const submit = () => {
    if (!canSubmit) return;
    onStart(
      mode === "url" ? { url: url.trim() } : { description: text.trim() },
    );
  };

  return (
    <OnboardingLayout
      step="website"
      title={
        mode === "url"
          ? t("steps.website.title")
          : t("steps.website.describeTitle")
      }
      intro={t("steps.website.intro")}
      footer={
        <div className="space-y-3 pt-2">
          <Button
            type="button"
            className="w-full"
            onClick={submit}
            disabled={!canSubmit}
          >
            <SparkleIcon className="h-4 w-4" weight="fill" />
            {isStarting ? t("actions.saving") : t("steps.website.analyze")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => setMode(mode === "url" ? "description" : "url")}
          >
            {mode === "url"
              ? t("steps.website.noWebsite")
              : t("steps.website.haveWebsite")}
          </Button>
        </div>
      }
    >
      {mode === "url" ? (
        <div className="space-y-2">
          <Label htmlFor="website">{t("steps.website.label")}</Label>
          <div className="relative">
            <GlobeIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="website"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") submit();
              }}
              placeholder={t("steps.website.placeholder")}
              className="pl-9"
              autoFocus
              autoComplete="url"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("steps.website.hint")}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="description">
            {t("steps.website.describeLabel")}
          </Label>
          <Textarea
            id="description"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={t("steps.website.describePlaceholder")}
            rows={4}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            {t("steps.website.describeHint")}
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <p className="font-medium text-destructive">
            {t("steps.website.errorTitle")}
          </p>
          <p className="mt-1 text-muted-foreground">{error}</p>
        </div>
      )}
    </OnboardingLayout>
  );
}
